import { useEffect, useState } from 'react'

import { fetchLocalJSON } from '../../../shared/utils/fetchLocalJSON.js'
import { Logger }         from '../../../shared/utils/logger.js'
import { FETCH_STATUS }   from '../../../shared/constants/fetchStatus.js'

const EMPTY_INTERIORS = Object.freeze({})

/**
 * Loads the optional interiors dictionary from the project data package.
 *
 * The source file is `interiors/interiors.json`. On success, the hook exposes
 * interiors dictionary as `data`. Missing configuration, an
 * unavailable or empty file, or another loading failure produces an empty
 * dictionary with `status` set to
 * `FETCH_STATUS.ERROR`. Consumers can therefore disable interiors-dependent
 * features without treating the entire inventory as unavailable.
 *
 * A change of `dataUrl` resets the resource to its loading state and starts a
 * new request. Results from an obsolete request are ignored after cleanup.
 *
 * @param {Object} params
 * @param {string|null|undefined} params.dataUrl - Base URL of the local project data package.
 * @param {boolean|null|undefined} params.enabled - Base URL of the local project data package.
 * @returns {{
 *   data: Record<string, unknown>,
 *   status: 'loading'|'ready'|'error',
 *   error: Error|null,
 * }} Interiors data and its loading state. The returned object remains stable until the state changes.
 */
export function useInteriors({ dataUrl, enabled }) {
  const [state, setState] = useState({
    data: EMPTY_INTERIORS,
    status: FETCH_STATUS.LOADING,
    error: null,
  })

  useEffect(() => {
    if (!enabled) {
        setState({
        data: EMPTY_INTERIORS,
        status: FETCH_STATUS.DISABLED,
        error: null,
        })
        return
    }
    if (!dataUrl) {
      setState({
        data: EMPTY_INTERIORS,
        status: FETCH_STATUS.ERROR,
        error: new Error('Project data URL is unavailable'),
      })
      Logger.error({
        message: 'Project data URL is unavailable'
      })
      return
    }

    let cancelled = false

    setState({
      data: EMPTY_INTERIORS,
      status: FETCH_STATUS.LOADING,
      error: null,
    })

    fetchLocalJSON(
      `${dataUrl}interiors/interiors.json`,
      'InventoryContext: interiors.json',
    )
      .then(data => {
        if (cancelled) return

        if (!data) {
          setState({
            data: EMPTY_INTERIORS,
            status: FETCH_STATUS.ERROR,
            error: new Error('interiors.json not found or empty'),
          })
          Logger.error({message:'interiors.json not found or empty'})
          return
        }
        
        setState({
          data: data,
          status: FETCH_STATUS.READY,
          error: null,
        })
        Logger.info({message:'interiors.json loaded from .dev_data/'})
      })
      .catch(error => {
        if (cancelled) return

        setState({
          data: EMPTY_INTERIORS,
          status: FETCH_STATUS.ERROR,
          error: error instanceof Error
            ? error
            : new Error(String(error)),
          })
        Logger.error({message:'error loading interiors.json', content: error})
      })

    return () => {
      cancelled = true
    }
  }, [dataUrl, enabled])

  return state
}
